define('CustomerOrderSurcharge.EntryPoint',
  [
    'CustomerOrderSurcharge.Model',
    'Cart.Summary.View',
    'Cart.Item.Actions.View',
    'Cart.Item.Summary.View',
    'LiveOrder.Model',
    'Profile.Model',
    'underscore'
  ],
  function (
    CustomerOrderSurchargeModel,
    CartSummaryView,
    CartItemActionsView,
    CartItemSummaryView,
    LiveOrderModel,
    ProfileModel,
    _
  ) {
    'use strict';

    return {
      mountToApp: function mountToApp(container) {
        var environment = container.getComponent('Environment');
        var config = environment.getConfig('customerOrderSurcharge');

        if (!config || !config.enabled) { return; }
        if (window.location.hostname.indexOf('henselite') === -1) { return; }

        var cart = container.getComponent('Cart');
        var liveOrder = LiveOrderModel.getInstance();
        var profileModel = ProfileModel.getInstance();
        var categories = config.customerCategories || [];
        var bannerMessage = config.bannerMessage || '';

        var surchargeItemIds = {};
        _.each(categories, function (cat) {
          if (cat.itemId) {
            surchargeItemIds[String(cat.itemId)] = true;
          }
        });

        // null   = not fetched yet
        // false  = fetched, customer exempt or no category
        // object = fetched, active category config
        var categoryConfig = null;
        var isFetching = false;
        var isUpdating = false;

        // --- Helpers ---

        function getSurchargeLine() {
          var lines = liveOrder.get('lines');
          if (!lines) { return null; }
          return lines.find(function (line) {
            var item = line.get('item');
            if (!item) { return false; }
            var id = String(item.get ? item.get('internalid') : item.internalid);
            return !!surchargeItemIds[id];
          });
        }

        function getCleanSubtotal() {
          var lines = liveOrder.get('lines');
          var total = 0;
          if (!lines) { return 0; }
          lines.each(function (line) {
            var item = line.get('item');
            if (!item) { return; }
            var id = String(item.get ? item.get('internalid') : item.internalid);
            if (!surchargeItemIds[id]) {
              total += parseFloat(line.get('amount') || 0);
            }
          });
          return total;
        }

        function isSurchargeModel(model) {
          var item = model && model.get('item');
          if (!item) { return false; }
          var itemId = String(item.get ? item.get('internalid') : item.internalid);
          return !!surchargeItemIds[itemId];
        }

        function addSurcharge() {
          if (isUpdating || !categoryConfig || !categoryConfig.itemId) { return; }
          isUpdating = true;

          cart.addLine({
            line: {
              quantity: 1,
              item: { internalid: categoryConfig.itemId }
            }
          }).always(function () {
            isUpdating = false;
          });
        }

        function removeSurcharge(surchargeLine) {
          if (isUpdating || !surchargeLine) { return; }
          isUpdating = true;

          cart.removeLine({
            line_id: surchargeLine.get('internalid')
          }).always(function () {
            isUpdating = false;
          });
        }

        function checkAndApplySurcharge() {
          if (isUpdating || !categoryConfig) { return; }
          var threshold = parseFloat(categoryConfig.threshold) || 0;
          if (!threshold || !categoryConfig.itemId) { return; }

          var surchargeLine = getSurchargeLine();
          var cleanSubtotal = getCleanSubtotal();

          if (cleanSubtotal > 0 && cleanSubtotal < threshold && !surchargeLine) {
            addSurcharge();
          } else if ((cleanSubtotal === 0 || cleanSubtotal >= threshold) && surchargeLine) {
            removeSurcharge(surchargeLine);
          }
        }

        // Fetches the customer category once per session.
        // Calls callback when resolved — guaranteed to run after the fetch lands,
        // so checkAndApplySurcharge always sees the correct categoryConfig.
        // In-flight requests are deduplicated: subsequent change:lines events
        // while a fetch is running are ignored; the completion calls the callback.
        function fetchCategoryIfNeeded(customerId, callback) {
          if (!customerId || profileModel.get('isLoggedIn') === 'F') { return; }

          if (categoryConfig !== null) {
            callback();
            return;
          }

          if (isFetching) { return; }

          isFetching = true;
          var model = new CustomerOrderSurchargeModel();
          model.fetch({
            data: {
              action: 'getCustomerCategory',
              customerId: customerId
            }
          }).then(function (response) {
            isFetching = false;
            var categoryId = response && response.category_id;

            if (!categoryId) {
              categoryConfig = false;
              return;
            }

            categoryConfig = _.find(categories, function (cat) {
              return cat.id === String(categoryId);
            }) || false;

            callback();
          }).fail(function () {
            // Leave categoryConfig as null so the next change:lines can retry
            isFetching = false;
          });
        }

        // --- Core listener — registered at shopping app start ---
        // Fetch is lazy: triggered by the first cart change after login,
        // ensuring the category is always resolved before checkAndApplySurcharge runs.

        liveOrder.on('change:lines', function () {
          if (isUpdating) { return; }
          fetchCategoryIfNeeded(
            profileModel.get('internalid'),
            checkAndApplySurcharge
          );
        });

        // --- View extensions ---

        // Hide Edit / Save for Later / Remove on surcharge lines (cart_item_actions.tpl)
        _.extend(CartItemActionsView.prototype, {
          render: _.wrap(CartItemActionsView.prototype.render, function (fn) {
            var result = fn.apply(this, _.toArray(arguments).slice(1));
            if (isSurchargeModel(this.model)) {
              this.$('.cart-item-actions-links').hide();
            }
            return result;
          })
        });

        // Hide quantity widget on surcharge lines (cart_item_summary.tpl)
        _.extend(CartItemSummaryView.prototype, {
          render: _.wrap(CartItemSummaryView.prototype.render, function (fn) {
            var result = fn.apply(this, _.toArray(arguments).slice(1));
            if (isSurchargeModel(this.model)) {
              this.$('.cart-item-summary-item-list-actionable-qty').hide();
            }
            return result;
          })
        });

        // Banner injected into CartSummary.AfterTitle slot (cart_summary.tpl).
        // Shown purely based on whether the surcharge line is in the cart —
        // no dependency on categoryConfig, so it works on first render too.
        _.extend(CartSummaryView.prototype, {
          render: _.wrap(CartSummaryView.prototype.render, function (fn) {
            var result = fn.apply(this, _.toArray(arguments).slice(1));
            this._updateSurchargeBanner();
            return result;
          }),

          _updateSurchargeBanner: function () {
            var slot = this.$('[data-view="CartSummary.AfterTitle"]');
            if (!slot.length) { return; }

            if (getSurchargeLine()) {
              if (!slot.find('.surcharge-banner').length) {
                slot.html(
                  '<div class="surcharge-banner" data-type="surcharge-banner">' +
                    '<p>' + bannerMessage + '</p>' +
                  '</div>'
                );
              }
              slot.find('.surcharge-banner').show();
            } else {
              slot.find('.surcharge-banner').hide();
            }
          }
        });
      }
    };
  }
);
